import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { capitalize } from "@mui/material";
import { RootState } from "@store/store";
import React, { FormEvent, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import SimpleInput from "src/Components/SimpleInput";
import ToggleRadioBtn from "src/Components/ToggleRadioBtn";
import httpRequest from "src/helpers/httpRequest";
import { weekDays } from "src/lib/constans";
import { IAvailability, IDoctorProfile } from "src/types/GlobalInterfaces";

interface IProps {
  previous: IAvailability;
  onSuccess: (val: IAvailability) => void;
  uid: string;
  profile: IDoctorProfile;
}

const DoctorOnlineAvaiabilityEdit: React.FC<IProps> = ({
  previous = null,
  onSuccess = () => {},
  uid,
  profile,
}) => {
  // const { profile } = useSelector((store: RootState) => store.doctor);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [availability, setAvailability] = useState<IAvailability>(
    previous || { fees: "" }
  );
  const handleToggle = (day: keyof IAvailability) => {
    const isExist = Object.keys(availability).includes(day);
    if (isExist) {
      setAvailability((prev) => {
        const updatedAvailability = { ...prev } as Record<string, any>;
        delete updatedAvailability[day];
        return updatedAvailability;
      });
    } else {
      setAvailability((prev) => ({
        ...prev,
        [day]: [["", ""]],
      }));
    }
  };

  const handleChange = (
    day: keyof IAvailability,
    index: number,
    type: number,
    value: string
  ) => {
    setAvailability((prev) => {
      const currentSchedule = Array.isArray(prev[day])
        ? (prev[day] as [string, string][])
        : [];
      const updatedSlots = currentSchedule.map((slot) => [...slot]);

      // Update the slot with the new value
      updatedSlots[index][type] = value;

      // Function to convert time string to number of minutes
      const timeToMinutes = (time: string): number => {
        if (!time) return 0;
        const [hours, minutes] = time.split(":").map(Number);
        return hours * 60 + minutes;
      };

      // Function to convert minutes to 12-hour format (hh:mm A)
      const minutesTo12HourFormat = (minutes: number): string => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const period = hours >= 12 ? "PM" : "AM";
        const hour12 = hours % 12 || 12;
        return `${hour12}:${mins.toString().padStart(2, "0")} ${period}`;
      };

      // Check if 'To' time is greater than 'From' time
      const fromTime = updatedSlots[index][0];
      const toTime = updatedSlots[index][1];

      if (fromTime && toTime) {
        const fromMinutes = timeToMinutes(fromTime);
        const toMinutes = timeToMinutes(toTime);
        
        // Simple validation: End time must be greater than start time (same day only)
        if (toMinutes <= fromMinutes) {
          toast(
            `${capitalize(
              String(day)
            )}: End time must be greater than Start time.`,
            {
              type: "error",
            }
          );
          const prevState = currentSchedule.map((slot) => [...slot]);
          prevState[index][type] = "";
          return { ...prev, [day]: prevState };
        }

        // Check if times are within the same day (00:00 to 23:59)
        if (fromMinutes < 0 || fromMinutes > 1439 || toMinutes < 0 || toMinutes > 1439) {
          toast(
            `${capitalize(
              String(day)
            )}: Time must be between 12:00 AM and 11:59 PM.`,
            {
              type: "error",
            }
          );
          const prevState = currentSchedule.map((slot) => [...slot]);
          prevState[index][type] = "";
          return { ...prev, [day]: prevState };
        }
      }

      // Check for overlap with other time slots on the same day
      const newFrom = timeToMinutes(fromTime);
      const newTo = timeToMinutes(toTime);
      if (fromTime && toTime) {
        for (let i = 0; i < updatedSlots.length; i++) {
          if (i === index) continue; // Skip the current slot being edited
          const [otherFrom, otherTo] = updatedSlots[i];
          if (otherFrom && otherTo) {
            const otherFromMinutes = timeToMinutes(otherFrom);
            const otherToMinutes = timeToMinutes(otherTo);
            // Check for overlap: !(newTo <= otherFromMinutes || newFrom >= otherToMinutes)
            if (
              otherFromMinutes &&
              otherToMinutes &&
              ((newFrom >= otherFromMinutes && newFrom < otherToMinutes) ||
              (newTo > otherFromMinutes && newTo <= otherToMinutes))
            ) {
              toast(
                `${capitalize(
                  String(day)
                )}: Time slot (${minutesTo12HourFormat(newFrom)} - ${minutesTo12HourFormat(newTo)}) overlaps with another slot (${minutesTo12HourFormat(otherFromMinutes)} - ${minutesTo12HourFormat(otherToMinutes)}).`,
                { type: "error" }
              );
              const prevState = currentSchedule.map((slot) => [...slot]);
              prevState[index][type] = "";
              return { ...prev, [day]: prevState };
            }
          }
        }
      }

      // Validate: Check for conflicts with hospital timings
      if (fromTime && toTime && profile?.timings) {
        for (const hospitalSchedule of profile.timings) {
          const hospitalTimings = hospitalSchedule[day];
          if (hospitalTimings && Array.isArray(hospitalTimings)) {
            for (const [hospitalFrom, hospitalTo] of hospitalTimings) {
              const hospitalFromMinutes = timeToMinutes(hospitalFrom);
              const hospitalToMinutes = timeToMinutes(hospitalTo);

              if (
                hospitalFromMinutes &&
                hospitalToMinutes &&
                ((newFrom >= hospitalFromMinutes && newFrom < hospitalToMinutes) ||
                (newTo > hospitalFromMinutes && newTo <= hospitalToMinutes))
              ) {
                const formattedHospitalFrom =
                  minutesTo12HourFormat(hospitalFromMinutes);
                const formattedHospitalTo =
                  minutesTo12HourFormat(hospitalToMinutes);
                toast(
                  `${capitalize(
                    String(day)
                  )}: Online time slot conflicts with hospital schedule (${formattedHospitalFrom} - ${formattedHospitalTo}) at ${
                    hospitalSchedule.hospitalName
                  }.`,
                  { type: "error" }
                );
                const prevState = currentSchedule.map((slot) => [...slot]);
                prevState[index][type] = "";
                return { ...prev, [day]: prevState };
              }
            }
          }
        }
      }

      return { ...prev, [day]: updatedSlots };
    });
  };

  const addRow = (day: keyof IAvailability) => {
    setAvailability((prev) => {
      // Ensure `prev[day]` is an array or default to an empty array
      const currentSchedule = Array.isArray(prev[day])
        ? (prev[day] as [string, string][])
        : [];
      return {
        ...prev,
        [day]: [...currentSchedule, ["", ""]],
      };
    });
  };

  const deleteRow = (day: keyof IAvailability, index: number) => {
    setAvailability((prev) => {
      // Ensure `prev[day]` is an array or default to an empty array
      const currentSchedule = Array.isArray(prev[day])
        ? (prev[day] as [string, string][])
        : [];

      // Filter out the item at the specified index
      const updatedSchedule = currentSchedule.filter((_, i) => i !== index);

      return {
        ...prev,
        [day]: updatedSchedule,
      };
    });
  };
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoader(true);
    const { res, err } = await httpRequest<{
      message: string;
      availability: IAvailability;
    }>({
      method: "post",
      path: `/provider/admin/update-consultation/${uid}`,
      params: availability,
    });
    if (res) {
      const { message, availability } = res;
      toast("Online availability updated", { type: "success" });
      onSuccess(availability);
    } else {
      toast(err, { type: "error" }); // api error
    }
    setIsLoader(false);
  };

  return (
    <div className="parent-box">
      <div className="border-b border-[#707070] pt-2 pb-4">
        <p className="font-Poppins-SemiBold font-size-20px theme-color-green">
          Online availability days and times
        </p>
        <p className="font-Poppins-Regular font-size-14px theme-color-gren">
          Keep this up to date so you get enquiries that suit you.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="py-4">
        <div>
          <SimpleInput
            type="text"
            value={availability?.fees || ""}
            onChange={(val) => {
              if (/^\d+$/.test(val) || val === "") {
                // Allow only positive whole numbers
                setAvailability((prev) => ({ ...prev, fees: val }));
              }
            }}
            isLabel={true}
            labelText={"Charges"}
            isRequired={true}
            maxLength={6}
            extraStyle={{ borderColor: "#009d91" }}
          />
        </div>
        {weekDays.map((day) => (
          <div key={day} className="availability mt-4">
            <div className="avail pb-4">
              <div className="flex gap-3">
                <ToggleRadioBtn
                  toggleText={capitalize(day)}
                  isToggled={Object.keys(availability).includes(day)}
                  handleToggle={() => handleToggle(day as keyof IAvailability)}
                />
              </div>
              {Object.keys(availability).includes(day) && (
                <>
                  {Array.isArray(availability[day as keyof IAvailability])
                    ? (
                        availability[day as keyof IAvailability] as [
                          string,
                          string
                        ][]
                      ).map((item, index) => (
                        <div
                          className="flex w-full gap-4 mt-4 items-center"
                          key={index}
                        >
                          <div className="w-[30%]">
                            <SimpleInput
                              value={item[0]}
                              onChange={(val) =>
                                handleChange(
                                  day as keyof IAvailability,
                                  index,
                                  0,
                                  val
                                )
                              }
                              isLabel={true}
                              labelText={"Start Time"}
                              extraStyle={{ borderColor: "#009d91" }}
                              type="time"
                              isRequired={true}
                            />
                          </div>

                          <div className="w-[30%]">
                            <SimpleInput
                              value={item[1]}
                              onChange={(val) =>
                                handleChange(
                                  day as keyof IAvailability,
                                  index,
                                  1,
                                  val
                                )
                              }
                              isLabel={true}
                              labelText={"End Time"}
                              extraStyle={{ borderColor: "#009d91" }}
                              type="time"
                              isRequired={true}
                            />
                          </div>
                          {index !== 0 && (
                            <button
                              onClick={() =>
                                deleteRow(day as keyof IAvailability, index)
                              }
                              type="button"
                              className="ml-4 mt-[35px]"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                        </div>
                      ))
                    : null}
                  <div className="mt-4">
                    <button
                      onClick={() => addRow(day as keyof IAvailability)}
                      type="button"
                      className="font-Poppins-semiBold font-size-18px theme-color-green"
                    >
                      + Add another time
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
        <div className="my-5">
          <PrimaryBtn
            type="submit"
            loader={isLoader}
            ButtonText="Save"
            btnExtraStyle={{
              backgroundColor: "#004540",
              borderRadius: "50rem",
              marginLeft: "auto",
            }}
          />
        </div>
      </form>
    </div>
  );
};

export default DoctorOnlineAvaiabilityEdit;
